import axios from 'axios';

/**
 * Axios instance pre-configured for the Visibility Labs API.
 * In development, requests proxy through CRA's dev server to the Django backend.
 * In production, the /api prefix is served by the same domain.
 */
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach the current access token to every request.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Prevent concurrent refresh attempts.
let _refreshPromise = null;

async function tryRefreshToken() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) return false;
    try {
      // Use plain axios (not the `api` instance) to avoid triggering this interceptor again.
      const { data } = await axios.post('/api/aivis/auth/token/refresh/', { refresh });
      if (data.access) {
        localStorage.setItem('access_token', data.access);
        return true;
      }
    } catch {
      // Refresh token itself is expired or invalid — fall through to logout.
    }
    return false;
  })();
  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

function forceLogout() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.location.assign('/ai-vis/login');
}

/**
 * Response interceptor — AI-Vis routes:
 *  1. On 401, try to silently refresh the access token.
 *  2. If refresh succeeds, retry the original request once.
 *  3. Only if refresh also fails, clear tokens and redirect to login.
 *
 * This prevents the user from being logged out just because their short-lived
 * access token expired while they were actively using the app.
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const reqUrl = error.config?.url || '';

    // Only intercept 401s outside of auth/token endpoints and only on AI-Vis pages.
    const isTokenEndpoint =
      reqUrl.includes('/aivis/auth/token/') ||
      reqUrl.includes('/aivis/auth/invite/');

    if (
      status === 401 &&
      !isTokenEndpoint &&
      !error.config?._retried &&
      typeof window !== 'undefined'
    ) {
      const path = window.location.pathname || '';
      const isAiVisPage =
        path.startsWith('/ai-vis') &&
        !path.startsWith('/ai-vis/login') &&
        !path.startsWith('/ai-vis/accept-invite');

      if (isAiVisPage) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          // Retry the original request with the new token.
          const retryConfig = {
            ...error.config,
            _retried: true,
            headers: {
              ...error.config.headers,
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          };
          return api(retryConfig);
        }
        // Refresh failed — session is truly expired.
        forceLogout();
      }
    }

    return Promise.reject(error);
  }
);

export default api;
